import React from "react";

import { cn } from "lib/utils";

import {
  SkeletonBlock,
  SkeletonDivider,
  SkeletonHeading,
  SkeletonInput,
  SkeletonText,
} from "./SkeletonPrimitives";

type FormSkeletonProps = {
  fieldCount?: number;
  columns?: 1 | 2;
  sections?: number;
  className?: string;
  withActions?: boolean;
};

const FormSkeleton = ({
  fieldCount = 6,
  columns = 1,
  sections = 1,
  className,
  withActions = true,
}: FormSkeletonProps) => {
  const fieldsPerSection = Math.ceil(fieldCount / sections);

  const renderFields = (count: number) => (
    <div
      className={cn(
        "grid gap-4",
        columns === 2 ? "md:grid-cols-2" : "grid-cols-1",
      )}
    >
      {Array.from({ length: count }).map((_, index) => (
        <div key={`field-${index}`} className="space-y-2">
          <SkeletonText className="h-4 w-32 rounded-md" />
          <SkeletonInput />
        </div>
      ))}
    </div>
  );

  return (
    <div
      className={cn(
        "space-y-6 rounded-2xl border border-slate-200/70 bg-white/95 p-6",
        className,
      )}
      aria-hidden
      data-testid="form-skeleton"
    >
      {Array.from({ length: sections }).map((_, sectionIndex) => (
        <div key={`section-${sectionIndex}`} className="space-y-4">
          {sections > 1 && <SkeletonHeading className="h-6 w-48" />}
          {renderFields(fieldsPerSection)}
          {sectionIndex < sections - 1 && <SkeletonDivider />}
        </div>
      ))}

      {withActions && (
        <div className="flex flex-wrap items-center justify-end gap-3 pt-2">
          <SkeletonBlock className="h-10 w-28 rounded-md" />
          <SkeletonBlock className="h-10 w-32 rounded-md" />
        </div>
      )}
    </div>
  );
};

export default FormSkeleton;
